import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faSpinner,
  faPhone,
  faCircle,
  faShieldHalved,
} from '@fortawesome/free-solid-svg-icons';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { logo } from '../assets';
import { persistUtmFromUrl, submitFormRecall } from '../utils/form-submissions';
import PrivacyConsents, { emptyPrivacyConsents } from '../components/PrivacyConsents';

const BRAND = 'serviziobolletta.it';
const BOT_NAME = 'Servizio Bolletta';

interface QuizAnswers {
  tipo_fornitura: string;
  spesa_mensile: string;
  situazione: string;
}

interface ContactData {
  nome: string;
  cognome: string;
  telefono: string;
  privacy1: boolean;
  privacy2: boolean;
  privacy3: boolean;
}

type FormStatus = 'idle' | 'loading' | 'success' | 'error';
type Phase = 'chat' | 'form' | 'thanks';

interface ChatMessage {
  id: string;
  role: 'bot' | 'user';
  text: string;
}

interface QuestionOption {
  value: string;
  label: string;
}

interface ChatQuestion {
  key: keyof QuizAnswers;
  botText: string;
  options: QuestionOption[];
}

const QUESTIONS: ChatQuestion[] = [
  {
    key: 'tipo_fornitura',
    botText: 'Perfetto! Per iniziare, che tipo di fornitura ti interessa?',
    options: [
      { value: 'luce', label: 'Solo Luce' },
      { value: 'gas', label: 'Solo Gas' },
      { value: 'luce_gas', label: 'Luce e Gas' },
    ],
  },
  {
    key: 'spesa_mensile',
    botText: 'Quanto spendi in media al mese in bolletta?',
    options: [
      { value: 'meno_50', label: 'Meno di 50€' },
      { value: '50_100', label: '50€ – 100€' },
      { value: '100_150', label: '100€ – 150€' },
      { value: 'oltre_150', label: 'Oltre 150€' },
    ],
  },
  {
    key: 'situazione',
    botText: 'Qual è la tua situazione attuale?',
    options: [
      { value: 'cambiare', label: 'Voglio cambiare fornitore' },
      { value: 'confrontare', label: 'Voglio solo confrontare' },
      { value: 'attivare', label: 'Devo attivare una nuova fornitura' },
    ],
  },
];

const ANSWER_LABELS: Record<string, Record<string, string>> = {
  tipo_fornitura: {
    luce: 'Solo Luce',
    gas: 'Solo Gas',
    luce_gas: 'Luce e Gas',
  },
  spesa_mensile: {
    meno_50: 'Meno di 50€',
    '50_100': '50€ – 100€',
    '100_150': '100€ – 150€',
    oltre_150: 'Oltre 150€',
  },
  situazione: {
    cambiare: 'Voglio cambiare fornitore',
    confrontare: 'Voglio solo confrontare',
    attivare: 'Devo attivare una nuova fornitura',
  },
};

const TypingIndicator: React.FC = () => (
  <div className="flex items-end gap-2">
    <div className="w-8 h-8 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
      <img src={logo} alt="" className="w-6 h-6 object-contain" />
    </div>
    <div className="bg-white rounded-2xl rounded-bl-md px-4 py-3 shadow-sm">
      <div className="flex gap-1.5 items-center h-4">
        {[0, 1, 2].map(i => (
          <motion.span
            key={i}
            className="w-2 h-2 rounded-full bg-gray-400"
            animate={{ y: [0, -4, 0], opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15 }}
          />
        ))}
      </div>
    </div>
  </div>
);

const ChatbotLanding: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('chat');
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [questionIndex, setQuestionIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [answers, setAnswers] = useState<QuizAnswers>({
    tipo_fornitura: '',
    spesa_mensile: '',
    situazione: '',
  });
  const [contact, setContact] = useState<ContactData>({
    nome: '',
    cognome: '',
    telefono: '',
    ...emptyPrivacyConsents(),
  });
  const [formStatus, setFormStatus] = useState<FormStatus>('idle');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const chatEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const initRef = useRef(false);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    });
  }, []);

  const addBotMessage = useCallback(
    (text: string, delay = 900): Promise<void> =>
      new Promise(resolve => {
        setIsTyping(true);
        setShowOptions(false);
        scrollToBottom();
        setTimeout(() => {
          setIsTyping(false);
          setMessages(prev => [
            ...prev,
            { id: `bot-${Date.now()}-${Math.random()}`, role: 'bot', text },
          ]);
          scrollToBottom();
          resolve();
        }, delay);
      }),
    [scrollToBottom],
  );

  useEffect(() => {
    persistUtmFromUrl();
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping, phase, showOptions, scrollToBottom]);

  useEffect(() => {
    if (initRef.current) return;
    initRef.current = true;

    const startChat = async () => {
      await addBotMessage(
        'Ciao! 👋 Sono l\'assistente di Servizio Bolletta. Ti aiuto a capire come risparmiare su luce e gas in pochi secondi.',
        1200,
      );
      await addBotMessage(QUESTIONS[0].botText, 800);
      setShowOptions(true);
    };

    startChat();
  }, [addBotMessage]);

  const handleOptionSelect = async (option: QuestionOption) => {
    if (!showOptions || isTyping) return;

    const question = QUESTIONS[questionIndex];
    setShowOptions(false);

    setMessages(prev => [
      ...prev,
      { id: `user-${Date.now()}`, role: 'user', text: option.label },
    ]);
    setAnswers(prev => ({ ...prev, [question.key]: option.value }));

    const nextIndex = questionIndex + 1;

    if (nextIndex < QUESTIONS.length) {
      await addBotMessage('Ottima scelta! ✅', 600);
      await addBotMessage(QUESTIONS[nextIndex].botText, 900);
      setQuestionIndex(nextIndex);
      setShowOptions(true);
    } else {
      await addBotMessage(
        'Perfetto, ho tutte le informazioni che mi servono! 🎉',
        700,
      );
      await addBotMessage(
        'Un consulente esperto può trovare per te l\'offerta più conveniente — gratuitamente e senza impegno. Lasciaci i tuoi dati e ti richiameremo entro 24 ore.',
        1000,
      );
      setPhase('form');
    }
  };

  const validateContact = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!contact.nome.trim()) newErrors.nome = 'Obbligatorio';
    if (!contact.cognome.trim()) newErrors.cognome = 'Obbligatorio';

    const tel = contact.telefono.replace(/[\s\-().+]/g, '');
    if (!tel) {
      newErrors.telefono = 'Obbligatorio';
    } else if (!/^\d{9,13}$/.test(tel)) {
      newErrors.telefono = 'Inserisci un numero valido (min. 9 cifre)';
    }

    if (!contact.privacy1) {
      newErrors.privacy1 = 'Devi accettare l\'informativa privacy e i termini e condizioni';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateContact()) return;

    setFormStatus('loading');

    const urlParams = new URLSearchParams(window.location.search);
    const idsms = urlParams.get('idsms');

    try {
      await submitFormRecall(
        {
          nome: contact.nome,
          cognome: contact.cognome,
          telefono: contact.telefono,
          privacy1: contact.privacy1,
          privacy2: contact.privacy2,
          privacy3: contact.privacy3,
        },
        {
          brand: BRAND,
          idsms,
          origine: 'quiz_offerta',
          extra: {
            tipo_fornitura: answers.tipo_fornitura,
            spesa_mensile: answers.spesa_mensile,
            situazione: answers.situazione,
            fonte: 'chatbot_landing',
          },
        },
      );

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'generate_lead',
        lead_source: 'chatbot_landing',
        lead_service: answers.tipo_fornitura,
        lead_spesa: answers.spesa_mensile,
      });

      setFormStatus('success');
      setPhase('thanks');
    } catch {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 4000);
    }
  };

  const formatAnswerSummary = (key: keyof QuizAnswers, value: string) =>
    ANSWER_LABELS[key]?.[value] ?? value;

  return (
    <>
      <Helmet>
        <title>Chat Assistente Bollette — Consulenza Gratuita Luce e Gas | Servizio Bolletta</title>
        <meta
          name="description"
          content="Parla con il nostro assistente e scopri come risparmiare sulle bollette luce e gas. Consulenza gratuita, senza obbligo. Rispondi in chat e ricevi un richiamo entro 24h."
        />
      </Helmet>

      <div className="h-[100dvh] flex flex-col bg-[#0b141a] overflow-hidden">
        {/* Header stile WhatsApp */}
        <header className="bg-[#075e54] text-white px-3 py-2.5 flex items-center gap-3 shadow-md z-20 flex-shrink-0 safe-top">
          <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center overflow-hidden flex-shrink-0">
            <img src={logo} alt={BOT_NAME} className="w-8 h-8 object-contain" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-semibold text-[15px] leading-tight truncate">{BOT_NAME}</h1>
            <p className="text-[12px] text-emerald-100/90 flex items-center gap-1.5">
              <FontAwesomeIcon icon={faCircle} className="text-[6px] text-[#25d366]" />
              Online — risponde subito
            </p>
          </div>
          <a
            href="tel:+390240055045"
            className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Chiama 02 4005 5045"
          >
            <FontAwesomeIcon icon={faPhone} className="text-sm" />
          </a>
        </header>

        {/* Area chat */}
        <div
          ref={chatContainerRef}
          className="flex-1 overflow-y-auto overscroll-contain px-3 py-4 space-y-3"
          style={{
            backgroundColor: '#ece5dd',
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4cdc4' fill-opacity='0.35'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        >
          {/* Badge informativo */}
          <div className="flex justify-center mb-2">
            <span className="bg-[#fff3cd] text-[#856404] text-[11px] px-3 py-1.5 rounded-lg shadow-sm text-center max-w-[280px] leading-snug">
              Consulenza gratuita e senza obbligo · Servizio indipendente
            </span>
          </div>

          <AnimatePresence initial={false}>
            {messages.map(msg => (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 12, scale: 0.96 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.25 }}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start items-end gap-2'}`}
              >
                {msg.role === 'bot' && (
                  <div className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                    <img src={logo} alt="" className="w-5 h-5 object-contain" />
                  </div>
                )}
                <div
                  className={`max-w-[85%] sm:max-w-[75%] px-3.5 py-2.5 text-[14px] leading-relaxed shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-[#dcf8c6] text-gray-900 rounded-2xl rounded-br-md'
                      : 'bg-white text-gray-800 rounded-2xl rounded-bl-md'
                  }`}
                >
                  {msg.text}
                  <p className={`text-[10px] mt-1 text-right ${msg.role === 'user' ? 'text-gray-500' : 'text-gray-400'}`}>
                    {new Date().toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {isTyping && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <TypingIndicator />
            </motion.div>
          )}

          {/* Form di ricontatto (stesso flusso di /offerta) */}
          {phase === 'form' && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex justify-start items-end gap-2"
            >
              <div className="w-7 h-7 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0 overflow-hidden">
                <img src={logo} alt="" className="w-5 h-5 object-contain" />
              </div>
              <div className="max-w-[92%] sm:max-w-[85%] w-full bg-white rounded-2xl rounded-bl-md shadow-md overflow-hidden">
                <div className="bg-[#075e54] px-4 py-3 text-white">
                  <p className="font-semibold text-sm">Richiedi il richiamo gratuito</p>
                  <p className="text-[11px] text-emerald-100/80 mt-0.5">
                    Compila i campi — ti contattiamo entro 24h
                  </p>
                </div>
                <div className="p-4 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Nome *</label>
                      <input
                        type="text"
                        value={contact.nome}
                        onChange={e => {
                          setContact(c => ({ ...c, nome: e.target.value }));
                          setErrors(er => ({ ...er, nome: '' }));
                        }}
                        className={`w-full px-3 py-2.5 rounded-lg border text-sm ${errors.nome ? 'border-red-400' : 'border-gray-200'} focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none`}
                        placeholder="Mario"
                      />
                      {errors.nome && <p className="text-red-500 text-[10px] mt-0.5">{errors.nome}</p>}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Cognome *</label>
                      <input
                        type="text"
                        value={contact.cognome}
                        onChange={e => {
                          setContact(c => ({ ...c, cognome: e.target.value }));
                          setErrors(er => ({ ...er, cognome: '' }));
                        }}
                        className={`w-full px-3 py-2.5 rounded-lg border text-sm ${errors.cognome ? 'border-red-400' : 'border-gray-200'} focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none`}
                        placeholder="Rossi"
                      />
                      {errors.cognome && <p className="text-red-500 text-[10px] mt-0.5">{errors.cognome}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Telefono *</label>
                    <input
                      type="tel"
                      inputMode="numeric"
                      value={contact.telefono}
                      onChange={e => {
                        const val = e.target.value.replace(/[^0-9\s\-+()]/g, '');
                        setContact(c => ({ ...c, telefono: val }));
                        setErrors(er => ({ ...er, telefono: '' }));
                      }}
                      onBlur={() => {
                        const tel = contact.telefono.replace(/[\s\-().+]/g, '');
                        if (tel && !/^\d{9,13}$/.test(tel)) {
                          setErrors(er => ({ ...er, telefono: 'Inserisci un numero valido (min. 9 cifre)' }));
                        }
                      }}
                      className={`w-full px-3 py-2.5 rounded-lg border text-sm ${errors.telefono ? 'border-red-400' : 'border-gray-200'} focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none`}
                      placeholder="333 1234567"
                      maxLength={16}
                    />
                    {errors.telefono && <p className="text-red-500 text-[10px] mt-0.5">{errors.telefono}</p>}
                  </div>

                  <PrivacyConsents
                    idPrefix="chatbot"
                    values={{
                      privacy1: contact.privacy1,
                      privacy2: contact.privacy2,
                      privacy3: contact.privacy3,
                    }}
                    onChange={consents => {
                      setContact(c => ({ ...c, ...consents }));
                      if (consents.privacy1) setErrors(er => ({ ...er, privacy1: '' }));
                    }}
                    privacy1Error={errors.privacy1}
                  />

                  {formStatus === 'error' && (
                    <div className="bg-red-50 text-red-600 text-xs p-2.5 rounded-lg">
                      Errore nell&apos;invio. Riprova o chiamaci al 02 4005 5045.
                    </div>
                  )}

                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    onClick={handleSubmit}
                    disabled={formStatus === 'loading'}
                    className="w-full bg-[#075e54] hover:bg-[#064e46] disabled:bg-gray-300 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
                  >
                    {formStatus === 'loading' ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} className="animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>Ricevi la tua consulenza gratuita</>
                    )}
                  </motion.button>

                  <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1">
                    <FontAwesomeIcon icon={faShieldHalved} className="text-gray-300" />
                    Dati protetti · Nessun obbligo di sottoscrizione
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Thank you */}
          {phase === 'thanks' && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex justify-center px-2"
            >
              <div className="bg-white rounded-2xl shadow-md p-5 max-w-sm w-full text-center">
                <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <FontAwesomeIcon icon={faCheck} className="text-green-600 text-xl" />
                </div>
                <h2 className="text-lg font-bold text-gray-900 mb-1">Grazie, {contact.nome}!</h2>
                <p className="text-sm text-gray-500 mb-4 leading-relaxed">
                  Ti richiameremo al <strong className="text-gray-700">{contact.telefono}</strong> entro 24 ore lavorative.
                </p>
                <div className="bg-gray-50 rounded-xl p-3 text-left space-y-2 mb-4">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide">Riepilogo</p>
                  <p className="text-xs text-gray-600">
                    <span className="text-gray-400">Fornitura:</span>{' '}
                    {formatAnswerSummary('tipo_fornitura', answers.tipo_fornitura)}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="text-gray-400">Spesa:</span>{' '}
                    {formatAnswerSummary('spesa_mensile', answers.spesa_mensile)}
                  </p>
                  <p className="text-xs text-gray-600">
                    <span className="text-gray-400">Esigenza:</span>{' '}
                    {formatAnswerSummary('situazione', answers.situazione)}
                  </p>
                </div>
                <Link to="/" className="text-[#075e54] hover:text-primary text-sm font-medium">
                  Torna alla homepage
                </Link>
              </div>
            </motion.div>
          )}

          <div ref={chatEndRef} />
        </div>

        {/* Opzioni di risposta rapida */}
        {phase === 'chat' && showOptions && !isTyping && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-[#f0f2f5] border-t border-gray-200 px-3 py-3 flex-shrink-0 safe-bottom"
          >
            <p className="text-[11px] text-gray-500 mb-2 text-center font-medium">Scegli un&apos;opzione</p>
            <div className={`grid gap-2 ${QUESTIONS[questionIndex].options.length > 3 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {QUESTIONS[questionIndex].options.map(opt => (
                <motion.button
                  key={opt.value}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => handleOptionSelect(opt)}
                  className="bg-white hover:bg-[#e7fce3] border border-[#25d366]/40 text-[#075e54] font-medium text-sm py-3 px-4 rounded-full shadow-sm transition-colors text-center"
                >
                  {opt.label}
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Footer disclaimer minimale */}
        <footer className="bg-[#f0f2f5] px-3 py-1.5 text-center flex-shrink-0 border-t border-gray-200">
          <p className="text-[9px] text-gray-400 leading-tight">
            GT Energie Srl · Servizio indipendente ·{' '}
            <Link to="/privacy-policy" className="underline hover:text-gray-600">Privacy</Link>
          </p>
        </footer>
      </div>
    </>
  );
};

export default ChatbotLanding;
